import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { useCashCandidates, useAdminAllPackages, useAdminAllPrices, type CashCandidate } from '../../api/admin';
import { CashPlanPicker, type CashPlanSelection } from '../../components/CashPlanPicker';
import { adminCashPlanChoices } from '../../lib/plan-catalog';
import {
  CASH_METHOD_LABEL,
  recordCashPayment,
  type CashMethod,
  type RecordCashResult,
} from '../../api/billing';
import { formatDate } from '../../lib/format';

const STATUS_TONE: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  trial: 'success',
  past_due: 'warning',
  suspended: 'error',
};

/** Manual cash payment — search a business, see what it owes, record the payment. */
export function CashEntry() {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<CashCandidate | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<CashMethod>('manual_cash');
  const [receiptRef, setReceiptRef] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<RecordCashResult | null>(null);
  // A business with NO live subscription: the payment STARTS a plan, so the plan must be named
  // (the API refuses otherwise, 2026-09-15). `hasLiveSubscription` is absent on an older API —
  // then the API's own 409 reveals the picker.
  const [plan, setPlan] = useState<CashPlanSelection | null>(null);
  const [planRequired, setPlanRequired] = useState(false);
  const needsPlan = !!selected && (selected.hasLiveSubscription === false || planRequired);
  const { data: allPackages = [] } = useAdminAllPackages();
  const { data: allPrices = [] } = useAdminAllPrices();
  const plans = needsPlan ? adminCashPlanChoices(allPackages, allPrices, selected?.resellerId ?? null) : [];

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data: candidates = [], isFetching } = useCashCandidates(debounced);

  function pick(c: CashCandidate) {
    setSelected(c);
    setAmount(c.owedMinor > 0 ? String(c.owedMinor / 100) : '');
    setPlan(null);
    setPlanRequired(false);
    setError(null);
    setDone(null);
  }

  function choosePlan(next: CashPlanSelection | null) {
    // Prefill the amount with the plan price when nothing (or the previous plan's price) is typed.
    const prevPrice = plan ? String(plan.priceMinor / 100) : '';
    if (next && (amount === '' || amount === prevPrice)) setAmount(String(next.priceMinor / 100));
    setPlan(next);
  }

  function reset() {
    setSelected(null);
    setQuery('');
    setDebounced('');
    setAmount('');
    setReceiptRef('');
    setNote('');
    setPlan(null);
    setPlanRequired(false);
    setError(null);
    setDone(null);
  }

  async function onRecord() {
    if (!selected) return;
    const amountMinor = parseCashAmountMinor(amount);
    if (amountMinor === null) {
      setError('Enter a valid amount in BDT (a positive number).');
      return;
    }
    if (needsPlan && !plan) {
      setError('Pick the plan the customer paid for — this business has no active subscription, so the payment starts one.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await recordCashPayment({
        businessId: selected.id,
        amountMinor,
        method,
        receiptRef: receiptRef.trim() || undefined,
        note: note.trim() || undefined,
        ...(needsPlan && plan ? { packageId: plan.packageId, interval: plan.interval } : {}),
      });
      setDone(res);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not record the payment. Please try again.';
      if (/no live subscription/i.test(message)) setPlanRequired(true);
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Record cash payment"
        subtitle="Mark a subscription paid when the customer paid by cash off-platform."
      />

      <div className="space-y-5">
        <Card className="p-5 sm:p-6">
          <CardHeader title="Find the business" />

          {!selected ? (
            <>
              <Input
                label="Business"
                required
                placeholder="Search by business name or slug (min 2 characters)…"
                prefix={<Search className="w-4 h-4" />}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
              {debounced.trim().length >= 2 && (
                <div className="mt-3 rounded-lg border border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-700 overflow-hidden">
                  {isFetching ? (
                    <div className="p-3 space-y-2">
                      <Skeleton className="h-10 rounded" />
                      <Skeleton className="h-10 rounded" />
                    </div>
                  ) : candidates.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
                      No business matches “{debounced.trim()}”.
                    </div>
                  ) : (
                    candidates.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pick(c)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{c.name}</span>
                          <span className="block text-xs text-zinc-500 dark:text-zinc-400 truncate">
                            {c.plan ?? 'No plan'}
                          </span>
                        </span>
                        <span className="flex items-center gap-3 flex-shrink-0">
                          <Badge tone={STATUS_TONE[c.status] ?? 'neutral'}>{c.status.replace(/_/g, ' ')}</Badge>
                          {c.owedMinor > 0 ? (
                            <span className="text-sm">
                              <span className="text-zinc-500 dark:text-zinc-400 mr-1">Owes</span>
                              <Money minor={c.owedMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                            </span>
                          ) : (
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">Nothing owed</span>
                          )}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50">{selected.name}</div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    {selected.plan ? `Plan: ${selected.plan}` : 'No plan'}
                    {selected.pastDueSince ? ` · Past due since ${formatDate(selected.pastDueSince)}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge tone={STATUS_TONE[selected.status] ?? 'neutral'}>{selected.status.replace(/_/g, ' ')}</Badge>
                  <span className="text-sm">
                    <span className="text-zinc-500 dark:text-zinc-400 mr-1">Owed:</span>
                    <Money minor={selected.owedMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                  </span>
                  {(selected.offerMinor ?? 0) > 0 && (
                    <span className="text-sm">
                      <span className="text-zinc-500 dark:text-zinc-400 mr-1">Upgrade offer:</span>
                      <Money minor={selected.offerMinor ?? 0} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={reset}
                    aria-label="Choose a different business"
                    className="w-7 h-7 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {selected && (
          <Card className="p-5 sm:p-6">
            <CardHeader title="Payment details" />
            <div className="space-y-4">
              {(selected.offerMinor ?? 0) > 0 && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                  This business has an open upgrade offer of ৳{((selected.offerMinor ?? 0) / 100).toLocaleString('en-US')}. A payment
                  settles it first: at least that much moves them to the new plan, and anything beyond it counts toward
                  the new plan&apos;s next renewal (a full renewal buys one more period; less is kept as credit). If they
                  are only renewing, record it after the offer lapses — it is payable for 7 days from when it was issued.
                </p>
              )}
              {needsPlan && (
                <CashPlanPicker
                  plans={plans}
                  value={plan ? { packageId: plan.packageId, interval: plan.interval } : null}
                  onChange={choosePlan}
                />
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label="Amount (BDT)"
                  required
                  prefix={<span className="text-sm">৳</span>}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  hint={
                    needsPlan && plan
                      ? `Starting a plan needs its full price — ৳${(plan.priceMinor / 100).toLocaleString('en-US')} for this one.`
                      : selected.owedMinor > 0 ? 'Prefilled with the open balance — adjust if the customer paid a different amount.' : undefined
                  }
                />
                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Method</label>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value as CashMethod)}
                    className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand"
                  >
                    {(Object.keys(CASH_METHOD_LABEL) as CashMethod[]).map((m) => (
                      <option key={m} value={m}>{CASH_METHOD_LABEL[m]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <Input
                label="Payment reference"
                value={receiptRef}
                onChange={(e) => setReceiptRef(e.target.value)}
                hint="Optional — office receipt #, bKash TrxID, bank slip, etc."
              />
              <div>
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Note</label>
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note stored with the payment"
                  className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 placeholder-zinc-400 dark:placeholder-zinc-500 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand resize-none"
                />
              </div>
            </div>
          </Card>
        )}

        {error && <Banner tone="error" title="Couldn’t record the payment">{error}</Banner>}
        {done ? (
          done.extended === false ? (
            <Banner tone="warning" title="Payment recorded — not renewed">
              {(done.creditMinor ?? 0) > 0 ? (
                <>
                  ৳{((done.creditMinor ?? 0) / 100).toLocaleString('en-US')} is kept as <strong>credit</strong>: it is less than
                  one billing period, so the subscription was not renewed and stays <strong>{done.status}</strong>. The credit
                  comes off the next invoice.
                </>
              ) : (
                <>
                  Applied as a part-payment to the open invoice — the business stays <strong>{done.status}</strong> until the
                  rest is paid.
                </>
              )}
            </Banner>
          ) : (
            <Banner tone="success" title={done.startedPlan ? 'Subscription started' : 'Payment recorded'}>
              {done.invoiceNo ? <>Invoice <span className="mono text-xs">{done.invoiceNo}</span> — </> : null}the business is
              now <strong>{done.status}</strong>{done.startedPlan ? <> on <strong>{done.startedPlan}</strong></> : null}
              {done.currentPeriodEnd ? <> (paid through {formatDate(done.currentPeriodEnd)})</> : null}.
            </Banner>
          )
        ) : selected ? (
          <Banner tone="info">
            Records the payment. Only when it covers what is owed — the open invoice, or one full billing period if nothing
            is open — does it renew the subscription one period (from the later of today / the current period end),
            reactivate a suspended business and notify the POS. A smaller amount is a part-payment, or is kept as credit.
          </Banner>
        ) : null}

        {selected && (
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="ghost" onClick={reset} disabled={submitting}>
              {done ? 'Record another' : 'Cancel'}
            </Button>
            <Button onClick={onRecord} loading={submitting} disabled={submitting || done !== null}>
              {done ? 'Recorded' : 'Record payment'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Parse the BDT amount field to integer minor units (poisha); null if not a positive number. */
export function parseCashAmountMinor(amountBdt: string): number | null {
  const n = Number(amountBdt.trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}
